import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Recycle, ArrowLeft, Download, Mail, TrendingDown, Building2, Package, Calendar, DollarSign, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { calculationsAPI, organizationsAPI } from '../api/client';
import { useToast } from '../hooks/use-toast';
import NotificationBell from '../components/NotificationBell';
import { format, parseISO } from 'date-fns';
import { lt } from 'date-fns/locale';

const CalculationDetail = () => {
  const { id } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [calculation, setCalculation] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [calcResponse, orgsResponse] = await Promise.all([
        calculationsAPI.getById(id),
        organizationsAPI.getAll()
      ]);
      setCalculation(calcResponse.data);
      setOrganizations(orgsResponse.data);
    } catch (error) {
      toast({
        title: "Klaida",
        description: "Nepavyko užkrauti skaičiavimo",
        variant: "destructive"
      });
      navigate('/history');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await calculationsAPI.downloadPDF(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tarsos_mokestis_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({
        title: "PDF atsisiųstas!",
        description: "Patikrinkite savo atsisiuntimus"
      });
    } catch (error) {
      toast({
        title: "Klaida",
        description: "Nepavyko atsisiųsti PDF",
        variant: "destructive"
      });
    }
  };

  const handleSendEmail = async () => {
    try {
      const response = await calculationsAPI.generatePDF(id);
      
      if (response.data.email_configured) {
        toast({
          title: "El. laiškas išsiųstas!",
          description: "Patikrinkite savo el. paštą"
        });
      } else {
        toast({
          title: "PDF sugeneruotas",
          description: "El. pašto paslauga dar nesukonfigūruota",
          variant: "default"
        });
      }
    } catch (error) {
      toast({
        title: "Klaida",
        description: "Nepavyko išsiųsti el. laiško",
        variant: "destructive"
      });
    }
  };

  const getWasteStreamLabel = (stream) => {
    const labels = {
      packaging: 'Pakuotės',
      weee: 'WEEE (elektros įranga)',
      oil: 'Alyvos',
      textile: 'Tekstilė'
    };
    return labels[stream] || stream;
  };

  const getPackagingTypeLabel = (type) => {
    const labels = {
      plastic: 'Plastikas',
      glass: 'Stiklas',
      paper: 'Popierius/Kartonas',
      metal: 'Metalas',
      composite: 'Kombinuotos'
    };
    return labels[type] || type;
  };

  const getOrganizationName = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    return org?.name || 'Nežinoma';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Kraunama...</p>
        </div>
      </div>
    );
  }

  if (!calculation) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Recycle className="w-8 h-8 text-green-600" />
            <span className="text-xl font-semibold text-gray-900">taršosmokestis.lt</span>
          </Link>
          <div className="flex items-center gap-4">
            <NotificationBell />
            <span className="text-sm text-gray-600">{user?.email}</span>
            <Button variant="outline" size="sm" onClick={logout}>Atsijungti</Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <div className="mb-6">
          <Link to="/history">
            <Button variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Grįžti į istoriją
            </Button>
          </Link>
        </div>

        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Skaičiavimo detalės
          </h1>
          <div className="flex items-center gap-4 text-gray-600">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{format(parseISO(calculation.created_at), 'yyyy-MM-dd HH:mm', { locale: lt })}</span>
            </div>
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              <span>{calculation.waste_results?.length || 0} atliekų srovės</span>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Summary Card */}
            <Card className="border-green-200 bg-green-50">
              <CardHeader>
                <CardTitle className="text-xl text-green-900 flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Mokesčio santrauka
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-green-200">
                    <p className="text-sm text-gray-600 mb-1">Bazinis mokestis</p>
                    <p className="text-2xl font-bold text-gray-900">
                      €{calculation.total_base_tax.toFixed(2)}
                    </p>
                  </div>

                  <div className="bg-white p-4 rounded-lg border border-green-200">
                    <p className="text-sm text-gray-600 mb-1">Nuolaida</p>
                    <p className="text-2xl font-bold text-green-600">
                      -€{calculation.total_discount.toFixed(2)}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-green-600 to-emerald-600 p-4 rounded-lg">
                    <p className="text-sm text-green-50 mb-1">Galutinis mokestis</p>
                    <p className="text-2xl font-bold text-white">
                      €{calculation.total_final_tax.toFixed(2)}
                    </p>
                  </div>
                </div>

                {calculation.total_discount > 0 && (
                  <div className="bg-white p-4 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 text-green-700 mb-2">
                      <TrendingDown className="w-5 h-5" />
                      <span className="font-semibold">
                        Sutaupyta {((calculation.total_discount / calculation.total_base_tax) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Prisijungus prie organizacijos, sutaupote €{calculation.total_discount.toFixed(2)} per metus
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Waste Results */}
            <Card>
              <CardHeader>
                <CardTitle>Atliekų srovių detalės</CardTitle>
                <CardDescription>
                  Išsamus kiekvienos atliekų srovės suskirstymas
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {calculation.waste_results?.map((result, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1">
                            {getWasteStreamLabel(result.waste_stream)}
                            {result.packaging_type && ` - ${getPackagingTypeLabel(result.packaging_type)}`}
                          </h4>
                          {result.organization_name && (
                            <p className="text-sm text-gray-600 flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {result.organization_name}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Svoris</p>
                          <p className="font-semibold">{result.weight_kg} kg</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div className="bg-gray-50 p-2 rounded">
                          <p className="text-gray-600 mb-1">Bazė</p>
                          <p className="font-semibold">€{result.base_tax.toFixed(2)}</p>
                        </div>
                        <div className="bg-green-50 p-2 rounded">
                          <p className="text-gray-600 mb-1">Nuolaida</p>
                          <p className="font-semibold text-green-600">-€{result.discount.toFixed(2)}</p>
                        </div>
                        <div className="bg-blue-50 p-2 rounded">
                          <p className="text-gray-600 mb-1">Galutinis</p>
                          <p className="font-semibold text-blue-600">€{result.final_tax.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Organization Comparison */}
            {calculation.potential_savings && Object.keys(calculation.potential_savings).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Organizacijų palyginimas
                  </CardTitle>
                  <CardDescription>
                    Galimas sutaupymas su kiekviena organizacija
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(calculation.potential_savings)
                      .sort((a, b) => b[1] - a[1])
                      .map(([orgId, savings]) => (
                        <div key={orgId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-center gap-3">
                            {calculation.best_organization === orgId && (
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            )}
                            <div>
                              <p className="font-medium text-gray-900">{getOrganizationName(orgId)}</p>
                              {calculation.best_organization === orgId && (
                                <p className="text-xs text-green-600">Geriausia pasirinkimas</p>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-green-600">€{savings.toFixed(2)}</p>
                            <p className="text-xs text-gray-500">sutaupymas</p>
                          </div>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Veiksmai</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  onClick={handleDownloadPDF}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Atsisiųsti PDF
                </Button>
                <Button
                  onClick={handleSendEmail}
                  variant="outline"
                  className="w-full"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Siųsti el. paštu
                </Button>
              </CardContent>
            </Card>

            {/* Info */}
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="text-lg text-blue-900 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Informacija
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-blue-900">
                <div>
                  <p className="font-semibold mb-1">Skaičiavimo ID</p>
                  <p className="text-xs font-mono text-blue-700 break-all">{calculation.id}</p>
                </div>
                <div>
                  <p className="font-semibold mb-1">Sukurta</p>
                  <p className="text-blue-700">
                    {format(parseISO(calculation.created_at), 'PPpp', { locale: lt })}
                  </p>
                </div>
                <div>
                  <p className="font-semibold mb-1">Atliekų srovių</p>
                  <p className="text-blue-700">{calculation.waste_results?.length || 0}</p>
                </div>
              </CardContent>
            </Card>

            {/* Tips */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Patarimai</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-gray-600">
                <p>• Reguliariai peržiūrėkite organizacijų pasiūlymus - kainos gali keistis</p>
                <p>• Saugokite ataskaitas dokumentacijai ir auditui</p>
                <p>• Nepamirškite pateikti metinės ataskaitos iki vasario 28 d.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalculationDetail;
